import { useEffect, useState } from 'react'
import { Canvas } from '@react-three/fiber'
import { canvasGlFactory } from '../lib/canvasRenderer.js'
import DebugEnvScene from './DebugEnvScene'

/**
 * Standalone ?debug_env=1 app — a minimal Canvas + DebugEnvScene, no avatar /
 * blueprint / network / gaussian characters. A focused harness to gauge how
 * well the baked environment lighting integrates and how the BecomingLit
 * shading equation reads on plain meshes.
 */
function Hud({ status }) {
  const [probe, setProbe] = useState({})
  const [dbg, setDbg] = useState(null)
  useEffect(() => {
    const id = setInterval(() => {
      setProbe(window.__LIGHT_PROBE_GRID__ || {})
      setDbg(window.__ENV_DEBUG__ || null)
    }, 250)
    return () => clearInterval(id)
  }, [])
  const row = (label, v) => (
    <div><span style={{ opacity: 0.6 }}>{label}:</span> {v}</div>
  )
  return (
    <div style={{
      position: 'absolute', top: 12, left: 12, zIndex: 10,
      font: '12px/1.5 var(--m-mono)', color: '#cde', background: 'rgba(0,0,0,0.55)',
      padding: '10px 12px', borderRadius: 6, maxWidth: 380, pointerEvents: 'none',
    }}>
      <div style={{ fontWeight: 600, color: '#9cf', marginBottom: 4 }}>env-debug · SH volume</div>
      {row('env', status.error ? `error: ${status.error}` : (status.env ?? '…'))}
      {row('SH grid', probe.state ?? '…')}
      {dbg && row('rig', dbg.pos.map(v => v.toFixed(2)).join(', '))}
      {dbg && row('ball means', dbg.balls.map(b => b.mean.toFixed(2)).join(' '))}
      <div style={{ marginTop: 6, opacity: 0.7 }}>
        arrows / PageUp-Down drive the rig · Shift = fast<br />
        balls L→R: chrome · roughness 0.05→1.0<br />
        tune: window.__ENV_DEBUG_DIFFUSE_SCALE__ / __ENV_DEBUG_SPEC_SCALE__
      </div>
    </div>
  )
}

export default function DebugEnvApp() {
  const [status, setStatus] = useState({})
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', background: '#000' }}>
      <Hud status={status} />
      <Canvas
        // THE SAME RENDERER THE APP BUILDS, or this harness cannot draw its subject.
        // A bare <Canvas> gets R3F's default classic WebGLRenderer, which compiles no
        // node material — so `GaussianSplat` is never drawn and the page renders the
        // probe balls over an empty room. `__THREE_ENV_SPLAT__.state` reported exactly
        // that ('not-drawn'), which is how this was found; the harness was simply left
        // behind when Spark stopped being the thing that drew the environment.
        gl={canvasGlFactory()}
        // The renderer itself, for a measurement. R3F keeps it in a store the
        // canvas element does not expose, and `renderer.info` is the only honest
        // answer to "what else is this frame doing" — draw calls, compute passes
        // and render targets, counted by three rather than guessed at.
        onCreated={({ gl }) => { window.__ENV_GL__ = gl }}
        camera={{ position: [1.4, 1.6, 1.3], fov: 50 }}
        dpr={[1, 1.5]}
        style={{ width: '100%', height: '100%' }}
      >
        <DebugEnvScene onStatus={s => setStatus(prev => ({ ...prev, ...s }))} />
      </Canvas>
    </div>
  )
}
